import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FaGraduationCap, FaChevronRight, FaBuilding, FaChalkboardTeacher, FaVial, FaBook, FaBullhorn, FaUsers, FaFlask
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import StatsCounter from './StatsCounter';


const SubLink = ({ text, href }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className="block bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white text-sm font-medium py-2 px-4 rounded-full transition-colors">
    {text}
  </a>
);

const Card = ({ icon, title, bgImage, children, href, className = "", subLinks = [] }) => {
  const isExternal = href && (href.startsWith('http') || href.startsWith('//'));
  
  const cardContent = (
    <div className="relative z-10 p-6 text-white h-full flex flex-col justify-end">
      <div className="transform-gpu transition-transform duration-500 ease-in-out group-hover:-translate-y-4">
        <div className="mb-3">
            {React.cloneElement(icon, { className: 'w-7 h-7 text-white opacity-90' })}
        </div>
        <h3 className="text-2xl font-bold leading-tight">{title}</h3>
      </div>
      <div className="mt-4 opacity-0 group-hover:opacity-100 transform translate-y-4 group-hover:translate-y-0 transition-all duration-300 ease-in-out">
        {children}
        {subLinks.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4">
            {subLinks.map((link, index) => (
              <SubLink key={index} text={link.text} href={link.href} />
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // Eğer subLinks varsa ve ana href yoksa, kartı sadece div olarak render et
  if (subLinks.length > 0 && !href) {
    return (
      <motion.div 
        className={`relative group rounded-2xl overflow-hidden shadow-lg transform hover:-translate-y-2 transition-transform duration-300 ${className}`}
        whileHover={{ scale: 1.03 }}
      >
        <div className="absolute inset-0 z-0">
          <img src={bgImage} alt={title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent transition-colors"></div>
        </div>
        {cardContent}
      </motion.div>
    );
  }

  return (
    <motion.div 
      className={`relative group rounded-2xl overflow-hidden shadow-lg transform hover:-translate-y-2 transition-transform duration-300 ${className}`}
      whileHover={{ scale: 1.03 }}
    >
      <div className="absolute inset-0 z-0">
        <img src={bgImage} alt={title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent transition-colors"></div>
      </div>
      {isExternal ? (
        <a href={href} target="_blank" rel="noopener noreferrer" className="absolute inset-0 z-20">
          {cardContent}
        </a>
      ) : (
        <Link to={href} className="absolute inset-0 z-20">
          {cardContent}
        </Link>
      )}
    </motion.div>
  );
};

const ShowcaseGrid = () => {
  const { t, i18n } = useTranslation();
  const prefix = i18n.language === 'en' ? '/en' : '';

  const cards = [
    {
      icon: <FaBuilding />,
      title: t('showcase.departments'),
      subLinks: [
        { text: t('showcase.actuarialScience'), href: "https://aktuerya.hacettepe.edu.tr/" },
        { text: t('showcase.biology'), href: "https://biology.hacettepe.edu.tr/" },
        { text: t('showcase.statistics'), href: "https://stat.hacettepe.edu.tr/" },
        { text: t('showcase.mathematics'), href: "https://mat.hacettepe.edu.tr/" },
        { text: t('showcase.chemistry'), href: "https://chem.hacettepe.edu.tr/" },
      ]
    },
    {
      icon: <FaGraduationCap />,
      title: t('showcase.academicPrograms'),
      href: "",
      subLinks: [
        { text: t('showcase.undergraduate'), href: prefix + "/programlar/lisans" },
        { text: t('showcase.masters'), href: prefix + "/programlar/yuksek-lisans" },
        { text: t('showcase.doctorate'), href: prefix + "/programlar/doktora" },
        { text: t('showcase.minor'), href: prefix + "/programlar/yandal" },
        { text: t('showcase.doubleMajor'), href: prefix + "/programlar/cift-anadal" },
        { text: t('showcase.erasmus'), href: prefix + "/programlar/erasmus" },
      ]
    },
    {
      icon: <FaVial />,
      title: t('showcase.researchLabs'),
      href: prefix + "/arastirma/laboratuvarlar",
      children: <p className="text-sm">{t('showcase.labsDesc')}</p>
    },
    {
      icon: <FaChalkboardTeacher />,
      title: t('showcase.faculty'),
      href: prefix + "/fakulte-personeli/ogretim-uyeleri",
      children: <p className="text-sm">{t('showcase.facultyDesc')}</p>
    },
    {
      icon: <FaBook />,
      title: t('showcase.journals'),
      href: prefix + "/arastirma/dergiler",
      children: <p className="text-sm">{t('showcase.journalsDesc')}</p>
    },
    {
      icon: <FaFlask />,
      title: t('showcase.ongoingProjects'),
      href: prefix + "/arastirma/devam-eden-projelerimiz",
      children: <p className="text-sm">{t('showcase.projectsDesc')}</p>
    }
  ];

  const cardImages = [
      "https://fs.hacettepe.edu.tr/fenyeni/books/MATEMAT%C4%B0K.jpg",
      "https://fs.hacettepe.edu.tr/fenyeni/books/hf.jpg",
      "https://lab.hacettepe.edu.tr/fs_/labs/hucrefizyolojisi1.jpg",
      "https://fs.hacettepe.edu.tr/fenyeni/books/hf1.jpg",
      "https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?auto=format&fit=crop&w=800&q=80",
      "https://images.unsplash.com/photo-1532094349884-543bc11b234d?auto=format&fit=crop&w=800&q=80"
  ]

  return (
    <>
      
      <div className="py-24 bg-gray-50">
        <div className="max-w-screen-xl mx-auto px-4">
          
          

          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">{t('showcase.title')}</h2>
            <p className="mt-4 text-lg leading-8 text-gray-600 max-w-2xl mx-auto">
              {t('showcase.subtitle')}
            </p>
          </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-[420px]">
          {cards.map((card, index) => (
            <Card
              key={index}
              {...card}
              bgImage={cardImages[index]}
            />
          ))}
        </div>

        <div className="mt-20">
          <StatsCounter />
        </div>
      </div>
    </div>
    </>
  );
};

export default ShowcaseGrid; 